import type { RemainderKind, SettlementTender } from '../wire';

/**
 * **The tender waterfall, previewed** — a line-by-line mirror of
 * `apps/core-api/src/waterfall.ts` (spec §6.5).
 *
 * ## Why a second implementation exists at all, and why it is not a second
 * source of truth
 *
 * dev.md rule 3 forbids a second scheduling engine, and for the same reason a
 * second *pricing* engine would be a bug: two answers to one question. This is
 * not that. The server computes the settlement; this computes **the number on
 * the till display while the owner is still typing**. It never leaves the
 * device, it is never sent, and nothing is stored from it — the request names
 * instruments, not amounts (`CreateSettlementBody` has no `total_cents` field to
 * put a client's arithmetic in), so a preview that were wrong could mislead the
 * person at the counter but could not corrupt a ledger.
 *
 * The alternative was a till that shows nothing until the network answers, which
 * on an iPad in a basement is a till that shows nothing. Spec §8 does not permit
 * that trade.
 *
 * ## How it stays pinned
 *
 * Three things hold the two implementations together, in descending order of how
 * much they are worth:
 *
 *  1. **`waterfall.test.ts` copies `apps/core-api/src/waterfall.test.ts`'s cases
 *     and its expected numbers verbatim.** Every ordering, cap, over-tender and
 *     pricing-regime case the server proves about itself is re-run against this
 *     function with the same expected output. A divergence fails here.
 *  2. **The structure is transcribed rather than reinvented** — same step order,
 *     same `draw` helper, same validation order, same `certificateExhausted`
 *     condition, same closing assertion that `Σ tenders === total`.
 *  3. **Refusals carry the server's own `reason` vocabulary** —
 *     `insufficient_funds`, `over_tender`, `invalid_amount`, `invalid_qty`,
 *     `no_lines`, `negative_total` — so the sentence the owner reads before
 *     submitting is the sentence they would have read afterwards.
 *
 * ## The one deliberate difference: refusals are returned, not thrown
 *
 * The server throws `ApiError`, because a request is one decision. A till is
 * keystrokes: an owner half-way through typing "50" has momentarily asked for $5
 * of a $4 certificate, and an exception per keystroke is not a user interface.
 * So this returns a discriminated result and the submit button reads `ok`.
 */

export type RefusalReason =
  | 'no_lines'
  | 'invalid_amount'
  | 'invalid_qty'
  | 'negative_total'
  | 'insufficient_funds'
  | 'over_tender'
  /** A split whose named parts leave something unpaid (0046). */
  | 'short_tender'
  | 'no_tender'
  | 'open_price_required';

/**
 * **One part of a split payment** — the server's `RemainderRequest`,
 * transcribed (0046, `docs/money-second-pass-design.md` §1).
 *
 * Every entry but the last names its amount; the last takes what is left, so
 * `Σ tenders = total + tip` holds by construction and the till's number and the
 * server's cannot drift apart over a rounding nobody performed.
 */
export interface RemainderRequest {
  kind: RemainderKind;
  cents?: number | undefined;
  label?: string | undefined;
}

/** One thing being sold, priced twice: what it lists at, and what is charged. */
export interface PreviewLine {
  itemId: string | null;
  itemName: string;
  qty: number;
  /** The catalog price, as this device last mirrored it. */
  listUnitCents: number;
  /** What the owner is actually charging per unit, on their own till. */
  chargedUnitCents: number;
  /**
   * **`items.open_price`: the list price is not a claim** (B4).
   *
   * A generic boutique line has no catalog price that means anything, so this
   * takes `list := charged` and the line's discount is zero by definition — the
   * server's rule, transcribed. The charged amount is therefore required and
   * positive, and the refusal is what the keypad's empty state reads as.
   */
  openPrice?: boolean | undefined;
  /** Staff free text about the thing sold — "coral scarf" (B4). */
  note?: string | undefined;
}

/**
 * An instrument the till may draw on.
 *
 * `cents` omitted means "whatever is left of it, capped at what is owed", which
 * is the ordinary case and never fails. Given means "exactly this much", which
 * is refused if the instrument does not hold it or the checkout does not owe it.
 */
export interface InstrumentRequest {
  cents?: number | undefined;
}

export interface PreviewInput {
  lines: readonly PreviewLine[];
  taxCents: number;
  manualDiscount?: InstrumentRequest | undefined;
  certificate?: (InstrumentRequest & { code: string; availableCents: number }) | undefined;
  deposit?: (InstrumentRequest & { availableCents: number }) | undefined;
  /** Gift is spent before cash. Fixed here as it is fixed on the server. */
  credit?:
    | {
        gift?: (InstrumentRequest & { availableCents: number }) | undefined;
        cash?: (InstrumentRequest & { availableCents: number }) | undefined;
      }
    | undefined;
  remainderKind: RemainderKind;
  /** The owner's own word for an `other` remainder — "Zelle" (B4). */
  remainderLabel?: string | undefined;
  /**
   * **The split**, when the cashier added a second payment (0046). Absent means
   * `[{ kind: remainderKind, label: remainderLabel }]` — one tender taking
   * whatever is left, which is what this function has always done and what
   * every case in `waterfall.test.ts` proves.
   */
  remainders?: readonly RemainderRequest[] | undefined;
  /**
   * **A tip** — money collected for a person, never the shop's (0046).
   *
   * Added to the remainder *after* every instrument has drawn against the sale,
   * exactly as on the server: `totalCents` stays the sale and
   * `Σ tenders = total + tip`. A guest cannot tip with the shop's own gift
   * certificate, and the arithmetic says so rather than a rule.
   */
  tipCents?: number | undefined;
}

export interface PreviewApplied {
  /** `Σ (list − charged) × qty` — the staff price override (§7.4 rule 3). */
  lineDiscountCents: number;
  manualDiscountCents: number;
  certificateCents: number;
  depositCents: number;
  /** A *discount*, never a tender (§7.4 rule 2). */
  giftCreditCents: number;
  /** A *payment*: the money was income when it was added. */
  cashCreditCents: number;
  /** The sale's rest **plus the tip** — what the external tenders settled. */
  remainderCents: number;
  /** Each part of the split, in the order the cashier entered them (0046). */
  remainders: SettlementTender[];
}

export interface PreviewLineResult {
  itemId: string | null;
  itemName: string;
  qty: number;
  /** The **list** price. A price change next year cannot rewrite this checkout. */
  unitPriceCents: number;
  discountCents: number;
  /** "coral scarf", when the till typed one (B4). */
  note?: string | undefined;
}

export interface PreviewTotals {
  /** `Σ list × qty` — the catalog's own number, never the charged one. */
  subtotalCents: number;
  /** Line overrides + manual discount + gift credit spent. */
  discountCents: number;
  taxCents: number;
  /** `subtotal − discount + tax` — **the sale**, with the tip outside it. */
  totalCents: number;
  /** Money for a person. `Σ tenders = total + tip` (0046). */
  tipCents: number;
  tenders: SettlementTender[];
  lines: PreviewLineResult[];
  applied: PreviewApplied;
  certificateExhausted: boolean;
}

export interface PreviewRefusal {
  reason: RefusalReason;
  /** The sentence — `core-api`'s, word for word. */
  message: string;
  /** Which step refused: `certificate`, `deposit`, `gift credit`, … */
  instrument?: string | undefined;
  requestedCents?: number | undefined;
  availableCents?: number | undefined;
  remainingCents?: number | undefined;
}

export type WaterfallPreview =
  ({ ok: true } & PreviewTotals) | { ok: false; refusal: PreviewRefusal };

function refuse(refusal: PreviewRefusal): WaterfallPreview {
  return { ok: false, refusal };
}

/**
 * Draw on one instrument.
 *
 * `remaining` is what the checkout still owes; `available` is what the
 * instrument holds. An omitted request takes the smaller of the two. An explicit
 * one is refused rather than clamped — a till that asks for something impossible
 * has a bug, and quietly giving it something else is how a drawer stops
 * balancing.
 */
function draw(
  what: string,
  request: InstrumentRequest | undefined,
  available: number,
  remaining: number,
): { cents: number } | { refusal: PreviewRefusal } {
  if (request === undefined) return { cents: 0 };
  const room = Math.max(0, Math.min(available, remaining));
  if (request.cents === undefined) return { cents: room };

  if (!Number.isInteger(request.cents) || request.cents < 0) {
    return {
      refusal: {
        reason: 'invalid_amount',
        instrument: what,
        message: `${what} must be a whole number of cents, not negative.`,
      },
    };
  }
  if (request.cents > available) {
    return {
      refusal: {
        reason: 'insufficient_funds',
        instrument: what,
        message: `There is not that much ${what} left.`,
        requestedCents: request.cents,
        availableCents: available,
      },
    };
  }
  if (request.cents > remaining) {
    return {
      refusal: {
        reason: 'over_tender',
        instrument: what,
        message: `That is more ${what} than this checkout owes.`,
        requestedCents: request.cents,
        remainingCents: remaining,
      },
    };
  }
  return { cents: request.cents };
}

/**
 * Split the remainder across the tenders the till named — the server's
 * `splitRemainder`, transcribed, with its refusals returned rather than thrown
 * (this file's one deliberate difference, argued at the top).
 *
 * The rule: *every entry but the last names its amount, and the last takes what
 * is left.* A remainder of zero yields no tenders at all, exactly as the
 * single-tender form always did.
 */
function splitRemainder(
  requested: readonly RemainderRequest[],
  remainderCents: number,
): { tenders: SettlementTender[] } | { refusal: PreviewRefusal } {
  if (requested.length === 0) {
    return {
      refusal: {
        reason: 'no_tender',
        message: 'A checkout needs at least one way to settle what is left.',
      },
    };
  }

  const tenders: SettlementTender[] = [];
  let left = remainderCents;

  for (const [index, part] of requested.entries()) {
    const isLast = index === requested.length - 1;
    let cents: number;

    if (part.cents === undefined) {
      if (!isLast) {
        return {
          refusal: {
            reason: 'invalid_amount',
            instrument: part.kind,
            message: 'Only the last payment may be “the rest”; name the others.',
          },
        };
      }
      cents = left;
    } else {
      if (!Number.isInteger(part.cents) || part.cents <= 0) {
        return {
          refusal: {
            reason: 'invalid_amount',
            instrument: part.kind,
            message: 'Every part of a payment is a positive whole number of cents.',
          },
        };
      }
      if (part.cents > left) {
        return {
          refusal: {
            reason: 'over_tender',
            instrument: part.kind,
            message: 'That is more than this checkout still owes.',
            requestedCents: part.cents,
            remainingCents: left,
          },
        };
      }
      cents = part.cents;
    }

    left -= cents;
    if (isLast && left > 0) {
      return {
        refusal: {
          reason: 'short_tender',
          message: 'That leaves part of this checkout unpaid.',
          remainingCents: left,
        },
      };
    }
    if (cents === 0) continue;

    tenders.push({
      kind: part.kind,
      amount_cents: cents,
      ...(part.kind === 'other' && part.label !== undefined && part.label.trim() !== ''
        ? { label: part.label.trim() }
        : {}),
    });
  }

  return { tenders };
}

export function previewWaterfall(input: PreviewInput): WaterfallPreview {
  if (input.lines.length === 0) {
    return refuse({ reason: 'no_lines', message: 'A settlement needs at least one line.' });
  }
  if (!Number.isInteger(input.taxCents) || input.taxCents < 0) {
    return refuse({
      reason: 'invalid_amount',
      message: 'Tax is a whole, non-negative number of cents.',
    });
  }
  const tipCents = input.tipCents ?? 0;
  if (!Number.isInteger(tipCents) || tipCents < 0) {
    return refuse({
      reason: 'invalid_amount',
      instrument: 'tip',
      message: 'A tip is a whole, non-negative number of cents.',
    });
  }

  /* ---- the lines, at list, with the override recorded as a discount ---- */

  let subtotalCents = 0;
  let lineDiscountCents = 0;
  const lines: PreviewLineResult[] = [];

  for (const line of input.lines) {
    if (!Number.isInteger(line.qty) || line.qty < 1) {
      return refuse({
        reason: 'invalid_qty',
        message: 'A line needs a whole quantity of at least one.',
      });
    }
    // The server's own refusal, transcribed: an open-price line whose amount
    // has not been typed yet. On a till that is *most keystrokes* — the line is
    // added before the keypad is used — so it is a returned refusal rather than
    // an exception, and the submit button simply stays off until it clears.
    if (
      line.openPrice === true &&
      !(Number.isInteger(line.chargedUnitCents) && line.chargedUnitCents > 0)
    ) {
      return refuse({
        reason: 'open_price_required',
        message: `${line.itemName} needs a price typed in.`,
        instrument: line.itemName,
      });
    }
    // `list := charged` for an open price, so the gap below is exactly zero.
    const listUnit = line.openPrice === true ? line.chargedUnitCents : line.listUnitCents;
    const list = listUnit * line.qty;
    // §7.4 rule 3, at the grain it is recorded: list minus charged, and the
    // *list* is what the line stores.
    const discount = (listUnit - line.chargedUnitCents) * line.qty;
    subtotalCents += list;
    lineDiscountCents += discount;
    lines.push({
      itemId: line.itemId,
      itemName: line.itemName,
      qty: line.qty,
      unitPriceCents: listUnit,
      discountCents: discount,
      ...(line.note === undefined || line.note === '' ? {} : { note: line.note }),
    });
  }

  /* ---- the waterfall, in the one order there is ---- */

  let remaining = subtotalCents - lineDiscountCents + input.taxCents;
  if (remaining < 0) {
    return refuse({
      reason: 'negative_total',
      message: 'A checkout cannot total less than nothing.',
    });
  }

  // 1. Manual discount. **Capped**, never refused: it is not an instrument with
  //    a balance, it is the owner deciding what this sale costs on their own
  //    till. "$100 off a $50 sale" means "it's free".
  const asked = input.manualDiscount?.cents ?? 0;
  if (!Number.isInteger(asked) || asked < 0) {
    return refuse({
      reason: 'invalid_amount',
      instrument: 'discount',
      message: 'A discount is a whole, non-negative number of cents.',
    });
  }
  const manualDiscountCents = Math.min(asked, remaining);
  remaining -= manualDiscountCents;

  // 2. Gift certificate. A *payment*, whose revenue was recognized the day it
  //    was sold (§7.4 rule 1) — so it is not new money today.
  const certificate = draw(
    'certificate',
    input.certificate,
    input.certificate?.availableCents ?? 0,
    remaining,
  );
  if ('refusal' in certificate) return refuse(certificate.refusal);
  remaining -= certificate.cents;

  // 3. Prepaid deposit. Same: income on `prepaid_at`, a tender today.
  const deposit = draw('deposit', input.deposit, input.deposit?.availableCents ?? 0, remaining);
  if ('refusal' in deposit) return refuse(deposit.refusal);
  remaining -= deposit.cents;

  // 4a. Gift credit — a DISCOUNT (§7.4 rule 2). Spent before cash credit,
  //     always: the shop's own goodwill is what gets used up first.
  const giftCredit = draw(
    'gift credit',
    input.credit?.gift,
    input.credit?.gift?.availableCents ?? 0,
    remaining,
  );
  if ('refusal' in giftCredit) return refuse(giftCredit.refusal);
  remaining -= giftCredit.cents;

  // 4b. Cash credit — a PAYMENT. It was income when it was added.
  const cashCredit = draw(
    'account credit',
    input.credit?.cash,
    input.credit?.cash?.availableCents ?? 0,
    remaining,
  );
  if ('refusal' in cashCredit) return refuse(cashCredit.refusal);
  remaining -= cashCredit.cents;

  // 5. Whatever is left, **plus the tip**, split across the tenders the till
  //    named. The only new money on the day (spec §6.5) minus the tip, which is
  //    somebody else's.
  const remainderCents = remaining + tipCents;

  /* ---- the document ---- */

  const discountCents = lineDiscountCents + manualDiscountCents + giftCredit.cents;
  const totalCents = subtotalCents - discountCents + input.taxCents;

  const tenders: SettlementTender[] = [];
  if (certificate.cents > 0 && input.certificate !== undefined) {
    tenders.push({
      kind: 'certificate',
      amount_cents: certificate.cents,
      ref: input.certificate.code,
    });
  }
  if (deposit.cents > 0) tenders.push({ kind: 'deposit', amount_cents: deposit.cents });
  if (cashCredit.cents > 0) {
    tenders.push({ kind: 'credit_cash', amount_cents: cashCredit.cents });
  }

  const split = splitRemainder(
    input.remainders ?? [
      {
        kind: input.remainderKind,
        ...(input.remainderLabel === undefined ? {} : { label: input.remainderLabel }),
      },
    ],
    remainderCents,
  );
  if ('refusal' in split) return refuse(split.refusal);
  tenders.push(...split.tenders);

  // The invariant both §7.4 reconcilers read structurally, generalized (0046):
  // the sale is `subtotal − discount + tax = total`, the money is
  // `Σ tenders = total + tip`. It holds by construction above; asserting it here
  // is what makes an edit that breaks the construction fail in the unit test
  // rather than in front of a guest.
  const tendered = tenders.reduce((sum, t) => sum + t.amount_cents, 0);
  if (tendered !== totalCents + tipCents) {
    throw new Error(
      `the till preview produced tenders of ${tendered} against a total of ${totalCents} ` +
        `plus a tip of ${String(tipCents)} (spec §7.4)`,
    );
  }

  return {
    ok: true,
    subtotalCents,
    discountCents,
    taxCents: input.taxCents,
    totalCents,
    tipCents,
    tenders,
    lines,
    applied: {
      lineDiscountCents,
      manualDiscountCents,
      certificateCents: certificate.cents,
      depositCents: deposit.cents,
      giftCreditCents: giftCredit.cents,
      cashCreditCents: cashCredit.cents,
      remainderCents,
      remainders: split.tenders,
    },
    certificateExhausted:
      input.certificate !== undefined &&
      certificate.cents > 0 &&
      certificate.cents >= input.certificate.availableCents,
  };
}
